/**
 * LandingPage - página inicial do MeSafa.ao
 */
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Sparkles,
    Calendar,
    BarChart3,
    Megaphone,
    Bot,
    ShieldCheck,
    Clock4,
    PlayCircle,
    Check,
    Star,
    ArrowRight,
    Zap,
} from "lucide-react";
import { CHANNELS } from "@/lib/channels";
import { HERO_IMG_1, SOCIAL_PROOF_IMG } from "@/lib/brand";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import {
    DEFAULT_BILLING_PERIOD,
    getPlanPricing,
    getPeriod,
    formatTotal,
    formatMonthly,
} from "@/lib/billing";
import BillingPeriodToggle from "@/components/shared/BillingPeriodToggle";

export default function LandingPage() {
    const [plans, setPlans] = useState([
        { id: "economico", name: "Económico", tagline: "Comece a sua jornada digital", price_aoa: 74900, price_usd: 87, period: "mês", features: ["1 rede social", "10 publicações por mês", "Calendário visual", "Estatísticas básicas", "Suporte por email"], highlight: false, contact_only: false },
        { id: "profissional", name: "Profissional", tagline: "Para negócios em crescimento", price_aoa: 164900, price_usd: 192, period: "mês", features: ["3 redes sociais", "Publicações ilimitadas", "IA para criação de conteúdo", "Agendamento automático", "Análise avançada", "Suporte prioritário"], highlight: true, contact_only: false },
        { id: "empresarial", name: "Empresarial", tagline: "Gestão completa para empresas", price_aoa: 299900, price_usd: 349, period: "mês", features: ["6 redes sociais", "Publicações ilimitadas", "IA avançada + estratégia", "Multi-utilizador", "Relatórios personalizados", "Gestor dedicado"], highlight: false, contact_only: false },
        { id: "agencia", name: "Agência", tagline: "Solução personalizada", price_aoa: 0, price_usd: 0, period: "mês", features: ["Clientes ilimitados", "White-label", "API dedicada", "SLA garantido", "Formação", "Contrato personalizado"], highlight: false, contact_only: true },
    ]);
    const [period, setPeriod] = useState(DEFAULT_BILLING_PERIOD);
    const { currency, toggle } = useCurrency();

    useEffect(() => {
        let cancelled = false;
        const fetchPlans = async (attempt = 1) => {
            try {
                const r = await api.get("/public/plans");
                if (!cancelled && r.data && r.data.length > 0) setPlans(r.data);
            } catch {
                if (!cancelled && attempt < 3) setTimeout(() => fetchPlans(attempt + 1), attempt * 5000);
            }
        };
        fetchPlans();
        return () => { cancelled = true; };
    }, []);

    const periodInfo = getPeriod(period);

    return (
        <div data-testid="landing-page">
            {/* HERO */}
            <section className="relative overflow-hidden bg-hero-radial-light">
                <div className="absolute inset-0 bg-noise opacity-30 pointer-events-none" />
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 pt-12 sm:pt-20 pb-14 sm:pb-24 relative">
                    <div className="grid lg:grid-cols-2 gap-10 items-center">
                        <div>
                            <Badge variant="outline" className="mb-5 border-[#6B21D9]/30 bg-[#6B21D9]/10 text-[#6B21D9] gap-1.5">
                                <Sparkles className="h-3.5 w-3.5" />
                                Feito em Angola · Para África
                            </Badge>
                            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
                                O <span className="text-[#6B21D9]">piloto automático</span> das suas redes sociais.
                            </h1>
                            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                                Crie, agende e publique em todas as redes sociais com a ajuda da nossa
                                IA. Foque-se no seu negócio. Nós tratamos da estratégia digital.
                            </p>
                            <div className="mt-8 flex flex-col sm:flex-row gap-3">
                                <Button
                                    asChild
                                    size="lg"
                                    className="btn-grad-purple text-white text-base h-12 px-6"
                                    data-testid="hero-primary-cta-button"
                                >
                                    <Link to="/registo" className="gap-2">
                                        Experimentar grátis <ArrowRight className="h-4 w-4" />
                                    </Link>
                                </Button>
                                <Button
                                    asChild
                                    size="lg"
                                    variant="outline"
                                    className="text-base h-12 px-6"
                                    data-testid="hero-secondary-cta-button"
                                >
                                    <Link to="/funcionalidades" className="gap-2">
                                        <PlayCircle className="h-4 w-4" /> Ver como funciona
                                    </Link>
                                </Button>
                            </div>
                            <div className="mt-8 flex items-center gap-6 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1.5">
                                    <Check className="h-4 w-4 text-[#2DD4BF]" /> 7 dias grátis
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <Check className="h-4 w-4 text-[#2DD4BF]" /> Sem cartão de crédito
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <Check className="h-4 w-4 text-[#2DD4BF]" /> Suporte em PT-PT
                                </span>
                            </div>
                        </div>

                        {/* Right: dashboard mockup card */}
                        <div className="relative">
                            <div className="absolute -inset-6 bg-[#6B21D9]/15 rounded-3xl blur-3xl pointer-events-none" />
                            <Card className="relative glass-card-light border-white/40 rounded-3xl overflow-hidden p-0">
                                <div className="px-5 py-3 border-b border-border flex items-center gap-2">
                                    <div className="flex gap-1.5">
                                        <span className="h-2.5 w-2.5 rounded-full bg-[#FB7185]" />
                                        <span className="h-2.5 w-2.5 rounded-full bg-[#FBBF24]" />
                                        <span className="h-2.5 w-2.5 rounded-full bg-[#2DD4BF]" />
                                    </div>
                                    <span className="text-xs text-muted-foreground ml-2">app.mesafa.ao/painel</span>
                                </div>
                                <div className="p-5 space-y-4">
                                    <div className="grid grid-cols-3 gap-3">
                                        {[
                                            { label: "Posts agendados", v: "23" },
                                            { label: "Alcance 30d", v: "48,2K" },
                                            { label: "Eng. médio", v: "4,8%" },
                                        ].map((k) => (
                                            <div key={k.label} className="rounded-xl bg-muted/40 p-3">
                                                <div className="text-[10px] text-muted-foreground uppercase">{k.label}</div>
                                                <div className="font-display text-xl font-semibold mt-1">{k.v}</div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/40">
                                        <div className="h-9 w-9 rounded-lg bg-[#6B21D9]/15 grid place-items-center text-[#6B21D9]">
                                            <Bot className="h-5 w-5" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="text-xs font-semibold">IA gerou 3 ideias para hoje</div>
                                            <div className="text-[11px] text-muted-foreground">Reel, Carrossel e Story · prontos a publicar</div>
                                        </div>
                                        <Sparkles className="h-4 w-4 text-[#6B21D9]" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <img
                                            src="https://images.pexels.com/photos/3026808/pexels-photo-3026808.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=300&w=300"
                                            alt=""
                                            className="h-28 w-full object-cover rounded-xl"
                                        />
                                        <img
                                            src="https://images.pexels.com/photos/1855214/pexels-photo-1855214.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=300&w=300"
                                            alt=""
                                            className="h-28 w-full object-cover rounded-xl"
                                        />
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>

            {/* CHANNELS STRIP */}
            <section className="border-y border-border bg-muted/30">
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
                    <p className="text-center text-xs text-muted-foreground uppercase tracking-wider mb-4">
                        Liga todas as suas redes sociais
                    </p>
                    <div className="flex items-center justify-center gap-6 sm:gap-12 flex-wrap">
                        {CHANNELS.map((c) => {
                            const Icon = c.icon;
                            return (
                                <div
                                    key={c.type}
                                    className="flex items-center gap-2 text-muted-foreground hover:text-[#6B21D9]"
                                >
                                    <Icon className="h-5 w-5" />
                                    <span className="text-sm font-medium">{c.name}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="py-16 sm:py-24">
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                            3 passos. E pronto.
                        </h2>
                        <p className="mt-3 text-muted-foreground">
                            É tão simples como contar até três. O resto fica connosco.
                        </p>
                    </div>
                    <div className="mt-10 grid md:grid-cols-3 gap-6">
                        {[
                            {
                                n: 1,
                                title: "Ligue as suas redes",
                                desc: "Conecte Facebook, Instagram, LinkedIn, TikTok, YouTube e X em segundos.",
                            },
                            {
                                n: 2,
                                title: "Deixe a IA criar",
                                desc: "Gere legendas, hashtags e imagens em português para a sua marca.",
                            },
                            {
                                n: 3,
                                title: "Agende e relaxe",
                                desc: "Calendarize publicações. Nós publicamos, medimos e otimizamos.",
                            },
                        ].map((s) => (
                            <Card key={s.n} className="p-6 rounded-2xl border-border bg-card">
                                <div className="font-display text-4xl font-bold text-[#6B21D9]">0{s.n}</div>
                                <h3 className="mt-3 font-semibold text-lg">{s.title}</h3>
                                <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURES BENTO */}
            <section className="py-16 sm:py-24 bg-muted/30">
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl mb-12">
                        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                            Tudo o que precisa, num só lugar.
                        </h2>
                        <p className="mt-3 text-muted-foreground">
                            Conteúdo, agendamento, anúncios, relatórios. A MeSafa é o seu estratega digital.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-5">
                        <FeatureBento
                            icon={Bot}
                            title="IA generativa em PT-PT"
                            desc="Legendas, hashtags, ideias e imagens criadas para o seu nicho."
                            big
                        />
                        <FeatureBento
                            icon={Calendar}
                            title="Calendário visual"
                            desc="Arraste e solte para reagendar. Veja tudo num só lugar."
                        />
                        <FeatureBento
                            icon={BarChart3}
                            title="Relatórios reais"
                            desc="Alcance, engagement, melhores horas e top posts."
                        />
                        <FeatureBento
                            icon={Megaphone}
                            title="Anuncios e Campanhas Ads"
                            desc="Orçamento, audiência e criativos. Tudo simples."
                        />
                        <FeatureBento
                            icon={Clock4}
                            title="Publicação automática"
                            desc="Definida a hora? Esqueça. Nós publicamos por si."
                        />
                        <FeatureBento
                            icon={ShieldCheck}
                            title="Suporte humano"
                            desc="Equipa angolana disponível para o ajudar em português."
                        />
                    </div>
                </div>
            </section>

            {/* SOCIAL PROOF */}
            <section className="py-16 sm:py-24">
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <Badge className="bg-[#6B21D9]/10 text-[#6B21D9] border-[#6B21D9]/30 mb-3">
                            Histórias reais
                        </Badge>
                        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                            “Poupei 12 horas por semana. E aumentei o alcance em 3x.”
                        </h2>
                        <p className="mt-4 text-muted-foreground">
                            Joana Tchindando, dona da Doce Mboa Pasteleira, deixou de perder fim-de-semana
                            a publicar nas redes. Hoje, a MeSafa publica por ela.
                        </p>
                        <div className="mt-5 flex items-center gap-1">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Star key={i} className="h-4 w-4 fill-[#FBBF24] text-[#FBBF24]" />
                            ))}
                            <span className="ml-2 text-sm text-muted-foreground">5.0 · 500+ empresas</span>
                        </div>
                    </div>
                    <div className="relative">
                        <img
                            src={SOCIAL_PROOF_IMG}
                            alt="Empresário angolano"
                            className="rounded-3xl w-full h-auto object-cover shadow-xl"
                        />
                    </div>
                </div>
            </section>

            {/* PRICING TEASER */}
            <section className="py-16 sm:py-24 bg-muted/30">
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
                        <div>
                            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                                Preços simples e justos.
                            </h2>
                            <p className="mt-2 text-muted-foreground">Comece grátis. Cresce connosco.</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                            <button
                                onClick={toggle}
                                className="inline-flex items-center gap-2 rounded-full bg-card border border-border px-1 py-1 text-sm"
                                data-testid="landing-pricing-currency-toggle"
                            >
                                {["AOA", "USD"].map((c) => (
                                    <span
                                        key={c}
                                        className={`px-3 py-1.5 rounded-full ${
                                            currency === c
                                                ? "bg-foreground text-background font-semibold"
                                                : "text-muted-foreground"
                                        }`}
                                    >
                                        {c}
                                    </span>
                                ))}
                            </button>

                            <BillingPeriodToggle
                                value={period}
                                onChange={setPeriod}
                                variant="light"
                                testId="landing-pricing-period-toggle"
                            />
                        </div>
                    </div>

                    {periodInfo.discount_pct > 0 && (
                        <p
                            className="mb-5 text-sm text-[#0F766E] font-medium"
                            data-testid="landing-pricing-period-discount-banner"
                        >
                            ✨ {periodInfo.tag} ao subscrever {periodInfo.short_label.toLowerCase()}.
                        </p>
                    )}

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {plans.map((p) => {
                            const isCustom = p.contact_only || p.id === "agencia";
                            const pricing = isCustom ? null : getPlanPricing(p, period);
                            const totalLabel = isCustom ? "Personalizado" : formatTotal(pricing, currency);
                            const monthlyLabel = isCustom ? null : formatMonthly(pricing, currency);

                            return (
                                <Card
                                    key={p.id}
                                    className={`p-6 rounded-2xl ${
                                        p.highlight ? "border-[#6B21D9] shadow-lg" : "border-border"
                                    } bg-card`}
                                    data-testid={`landing-pricing-${p.id}-card`}
                                >
                                    {p.highlight && (
                                        <Badge className="mb-3 btn-grad-purple text-white border-none">
                                            Mais escolhido
                                        </Badge>
                                    )}
                                    <h3 className="font-display text-xl font-semibold">{p.name}</h3>
                                    <p className="text-sm text-muted-foreground mt-1">{p.tagline}</p>
                                    <div className="mt-4 mb-5 min-h-[80px]">
                                        {isCustom ? (
                                            <span className="font-display text-xl font-bold">Personalizado</span>
                                        ) : (
                                            <>
                                                <div>
                                                    <span className="font-display text-3xl font-bold">{monthlyLabel}</span>
                                                    <span className="text-muted-foreground text-sm"> /mês</span>
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-1">
                                                    {periodInfo.months > 1
                                                        ? <>Total: <span className="font-medium text-foreground">{totalLabel}</span></>
                                                        : "Faturação mensal"}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <ul className="space-y-2 mb-5">
                                        {p.features.slice(0, 5).map((f, i) => (
                                            <li key={i} className="flex items-start gap-2 text-sm">
                                                <Check className="h-4 w-4 text-[#2DD4BF] shrink-0 mt-0.5" />
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <Button
                                        asChild
                                        className={`w-full ${p.highlight ? "btn-grad-purple text-white" : ""}`}
                                        variant={p.highlight ? "default" : "outline"}
                                        data-testid={`landing-pricing-${p.id}-cta`}
                                    >
                                        <Link to={isCustom ? "/contacto" : "/registo"}>
                                            {isCustom ? "Falar com vendas" : `Começar com ${p.name}`}
                                        </Link>
                                    </Button>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-20">
                <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8">
                    <div className="relative overflow-hidden rounded-3xl bg-[#0B0B10] text-white p-10 sm:p-14 text-center">
                        <div className="absolute inset-0 bg-hero-radial-dark pointer-events-none" />
                        <div className="absolute inset-0 bg-noise opacity-40 pointer-events-none" />
                        <div className="relative">
                            <Zap className="h-8 w-8 mx-auto text-[#A78BFA] mb-4" />
                            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                                Pronto para deixar o piloto automático tratar?
                            </h2>
                            <p className="mt-3 text-white/70 max-w-xl mx-auto">
                                Junte-se a centenas de empresas em Angola que deixaram de se preocupar com redes sociais.
                            </p>
                            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                                <Button
                                    asChild
                                    size="lg"
                                    className="btn-grad-purple text-white text-base h-12 px-6"
                                    data-testid="final-cta-register-button"
                                >
                                    <Link to="/registo">Experimentar grátis</Link>
                                </Button>
                                <Button
                                    asChild
                                    size="lg"
                                    variant="outline"
                                    className="text-base h-12 px-6 bg-white/5 border-white/15 text-white hover:bg-white/10"
                                >
                                    <Link to="/contacto">Falar com a equipa</Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function FeatureBento({ icon: Icon, title, desc, big = false }) {
    return (
        <Card
            className={`p-6 rounded-2xl border-border bg-card hover:border-[#6B21D9]/40 ${
                big ? "md:col-span-2 md:row-span-1" : ""
            }`}
        >
            <div className="h-10 w-10 rounded-xl bg-[#6B21D9]/10 grid place-items-center text-[#6B21D9] mb-4">
                <Icon className="h-5 w-5" />
            </div>
            <h3 className="font-semibold text-lg">{title}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
        </Card>
    );
}
